import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useQuery } from 'react-query'

import { getPricingPageUrl } from 'helpers/account'
import { CHINESE_LANGUAGE_CHECKOUT_LOCALES } from 'constants/account'
import { FREE_GROUP_CODE } from 'constants/registrations'

/**
 * Custom hook to manage purchase modal
 *
 * @returns {Object} An object containing:
 *  - showPurchaseModal: boolean - whether the purchase modal is showing
 *  - setShowPurchaseModal: function - setter for showing/hiding purchase modal
 *  - handlePurchasePlan: function - handles purchasing a new plan
 *  - isChinaUserWithoutPlan: boolean - whether user is in China without a plan
 *  - openPurchaseModal: function - directly opens the purchase modal
 *  - purchasePlans: object - the purchase plans data
 *  - isPurchasePlansLoading: boolean - whether the purchase plans are loading
 */
export const usePurchaseModal = () => {
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)

  const {
    user: { checkout_locale, selected_language, currency },
    membershipInfo: { group, hasActiveMembership }
  } = useSelector(state => state.globalData)

  const isChinaUserWithoutPlan =
    CHINESE_LANGUAGE_CHECKOUT_LOCALES.includes(checkout_locale) &&
    !hasActiveMembership

  const isChinaUserWithFreemiumPlan =
    CHINESE_LANGUAGE_CHECKOUT_LOCALES.includes(checkout_locale) &&
    hasActiveMembership &&
    group === FREE_GROUP_CODE

  // Use react-query to fetch purchase plans
  const {
    data: purchasePlans,
    isLoading: isPurchasePlansLoading,
    refetch: refetchPurchasePlans
  } = useQuery('api/purchase_plans', {
    enabled: false, // Don't fetch on component mount
    refetchOnWindowFocus: false
  })

  const handlePurchasePlan = () => {
    if (isChinaUserWithoutPlan) {
      refetchPurchasePlans()
      setShowPurchaseModal(true)
    } else {
      window.location.assign(
        getPricingPageUrl(checkout_locale, currency, selected_language, group)
      )
    }
  }

  // Function to handle opening the purchase modal directly
  const openPurchaseModal = () => {
    refetchPurchasePlans()
    setShowPurchaseModal(true)
  }

  return {
    showPurchaseModal,
    setShowPurchaseModal,
    handlePurchasePlan,
    isChinaUserWithoutPlan,
    openPurchaseModal,
    purchasePlans,
    isChinaUserWithFreemiumPlan,
    isPurchasePlansLoading
  }
}
